import type { KeyboardEvent as ReactKeyboardEvent } from 'react'

/**
 * Utility functions for handling keyboard events in table and form contexts
 */

/**
 * Checks if a keyboard event should be ignored because it's intended for input fields
 */
export function shouldIgnoreKeyboardEvent(
  event: KeyboardEvent | ReactKeyboardEvent,
): boolean {
  const target = event.target as HTMLElement

  if (!target) {
    return false
  }

  // Check if the target or any of its parents is an input field
  return isInputElement(target)
}

/**
 * Checks if an element is an input field or within an input context
 */
export function isInputElement(element: Element | HTMLElement | null): boolean {
  if (!element) {
    return false
  }

  // Direct input elements
  if (
    element.tagName === 'INPUT' ||
    element.tagName === 'TEXTAREA' ||
    element.tagName === 'SELECT'
  ) {
    return true
  }

  // Contenteditable elements
  if (
    element.getAttribute('contenteditable') === 'true' ||
    (element as HTMLElement).isContentEditable
  ) {
    return true
  }

  // ARIA roles that indicate text input
  const role = element.getAttribute('role')
  if (
    role === 'textbox' ||
    role === 'combobox' ||
    role === 'listbox' ||
    role === 'option'
  ) {
    return true
  }

  // Elements with specific data attributes or classes that indicate input behavior
  if (
    element.hasAttribute('aria-multiline') ||
    element.closest('[contenteditable="true"]') ||
    element.closest('[data-input-wrapper]') ||
    element.closest('[data-task-name-input]') ||
    element.closest('[data-radix-select-content]') ||
    element.closest('[data-radix-popover-content]') ||
    element.closest('[data-radix-dropdown-menu-content]') ||
    element.closest('[data-radix-context-menu-content]') ||
    element.closest('.input-field') ||
    element.closest('.editable-cell') ||
    element.closest('[data-editing="true"]')
  ) {
    return true
  }

  return false
}

/**
 * True when the element is (or sits inside) a link, button or role=button
 * that itself lives inside a table cell (`td[data-column]`). The table's
 * document-level keydown handler lets Enter, Space, Tab, Delete and
 * Backspace through to such elements so a focused row anchor can be
 * activated, while arrows, Escape, copy and paste stay with the cell grid.
 */
export function isInteractiveDescendant(
  element: Element | HTMLElement | null,
): boolean {
  if (!element || typeof element.closest !== 'function') {
    return false
  }

  const interactive = element.closest('a[href], button, [role="button"]')
  if (!interactive) {
    return false
  }

  return interactive.closest('td[data-column]') !== null
}

/**
 * True on macOS. Mirrors the `navigator.platform` check OmniSearch already uses
 * to render its ⌘K hint, so every surface agrees on the modifier glyph.
 */
export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false
  return (navigator.platform ?? '').includes('Mac')
}

/**
 * The label for the primary modifier: ⌘ on macOS, Ctrl elsewhere.
 */
export function getModifierKeyLabel(): string {
  return isMacPlatform() ? '⌘' : 'Ctrl'
}

/** Modifier glyphs macOS users expect; other platforms spell them out. */
const MAC_MODIFIER_GLYPHS: Record<string, string> = {
  Mod: '⌘',
  Shift: '⇧',
  Alt: '⌥',
  Ctrl: '⌃',
}

/**
 * Renders one key token for display, translating `Mod` to ⌘ / Ctrl and, on
 * macOS, the other modifiers to their glyphs.
 */
export function formatShortcutKey(key: string): string {
  if (isMacPlatform()) {
    return MAC_MODIFIER_GLYPHS[key] ?? key
  }
  return key === 'Mod' ? 'Ctrl' : key
}

/**
 * Renders a key combo for display. Used by the shortcut cheatsheet and by
 * tooltip shortcut hints so both read the same on a given platform.
 */
export function formatShortcutKeys(keys: string[]): string {
  const rendered = keys.map(formatShortcutKey)
  // Modifier glyphs sit flush against the next key on macOS (⌘K, ⇧⌘Z).
  return isMacPlatform() ? rendered.join('') : rendered.join('+')
}

/**
 * Prevents keyboard event handling for table/drag operations when user is typing in input fields
 */
export function createInputAwareKeyboardHandler<
  T extends KeyboardEvent | ReactKeyboardEvent,
>(handler: (event: T) => void) {
  return (event: T) => {
    if (!shouldIgnoreKeyboardEvent(event)) {
      handler(event)
    }
  }
}
